import type { Player, Team } from '../../store/config';

export type ImportResult = {
  players: Player[];
  teams: Team[];
  skipped: number;
};

function normalizeSteamId(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

/**
 * Quotes long integers before parsing.
 *
 * A Steam64 ID is 17 digits, well past Number.MAX_SAFE_INTEGER, so an unquoted
 * one in a JSON export loses its last digits the moment JSON.parse sees it —
 * 76561198000000002 becomes 76561198000000000, and the invite silently goes to
 * nobody. Quoting first keeps the digits exact.
 */
export function quoteLongNumbers(raw: string): string {
  return raw.replace(/(:\s*)(-?\d{16,})(\s*[,}\]])/g, '$1"$2"$3');
}

function makeTeamId(name: string): string {
  return `team-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

/**
 * Parses a bulk player import.
 *
 * Two shapes are accepted, both of which existed in the old client:
 *   - JSON: `{ "Team name": [{ "pseudo": "...", "steamId": "..." }] }`
 *   - lines: `name,steamId[,team]`
 *
 * Existing players are matched by Steam ID so re-importing a roster updates
 * names instead of duplicating people.
 */
export function parseBulkImport(
  raw: string,
  existingPlayers: Player[],
  existingTeams: Team[],
): ImportResult {
  const players = [...existingPlayers];
  const teams = [...existingTeams];
  let skipped = 0;

  const upsertPlayer = (name: string, steamIdRaw: unknown, teamName?: string) => {
    const steamId = normalizeSteamId(steamIdRaw);
    const playerName = name.trim();
    if (!steamId || !playerName) {
      skipped += 1;
      return;
    }

    const existingIndex = players.findIndex((player) => player.steamId === steamId);
    if (existingIndex >= 0) {
      players[existingIndex] = { name: playerName, steamId };
    } else {
      players.push({ name: playerName, steamId });
    }

    if (!teamName?.trim()) return;

    const cleanTeamName = teamName.trim();
    let team = teams.find((candidate) => candidate.name.toLowerCase() === cleanTeamName.toLowerCase());
    if (!team) {
      team = { id: makeTeamId(cleanTeamName), name: cleanTeamName, playerSteamIds: [] };
      teams.push(team);
    }
    if (!team.playerSteamIds.includes(steamId)) {
      team.playerSteamIds = [...team.playerSteamIds, steamId];
      teams[teams.indexOf(team)] = team;
    }
  };

  const text = raw.trim();
  if (!text) return { players, teams, skipped };

  try {
    const parsed: unknown = JSON.parse(quoteLongNumbers(text));

    if (Array.isArray(parsed)) {
      for (const entry of parsed) {
        if (!entry || typeof entry !== 'object') {
          skipped += 1;
          continue;
        }
        const record = entry as Record<string, unknown>;
        upsertPlayer(
          String(record.pseudo ?? record.name ?? ''),
          record.steamId ?? record.steam_id,
          typeof record.team === 'string' ? record.team : undefined,
        );
      }
      return { players, teams, skipped };
    }

    if (parsed && typeof parsed === 'object') {
      for (const [teamName, members] of Object.entries(parsed as Record<string, unknown>)) {
        if (!Array.isArray(members)) {
          skipped += 1;
          continue;
        }
        for (const member of members) {
          if (!member || typeof member !== 'object') {
            skipped += 1;
            continue;
          }
          const record = member as Record<string, unknown>;
          upsertPlayer(
            String(record.pseudo ?? record.name ?? ''),
            record.steamId ?? record.steam_id,
            teamName,
          );
        }
      }
      return { players, teams, skipped };
    }
  } catch {
    // Not JSON: fall through to the line format.
  }

  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const [name = '', steamId = '', teamName] = trimmed.split(',').map((part) => part.trim());
    upsertPlayer(name, steamId, teamName);
  }

  return { players, teams, skipped };
}
